import { useState, useEffect, useRef } from 'react';
import { chatAiAssistant, chatSales, getWhySnugglemind, getChatHistory, saveChatMessage } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  ChatCircleDots,
  X,
  PaperPlaneRight,
  Robot,
  Handshake,
  Sparkle,
  CaretDown,
  CaretRight,
  User,
  Spinner
} from '@phosphor-icons/react';

const TABS = [
  { key: 'ai', label: 'AI Assistant', icon: Robot, color: 'text-violet-600' },
  { key: 'sales', label: 'Chat with Sales', icon: Handshake, color: 'text-blue-600' },
  { key: 'why', label: 'Why Snugglemind', icon: Sparkle, color: 'text-emerald-600' },
];

function AiAssistantTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('snugglemind_chat_session');
    return stored || `chat_${Date.now()}`;
  });
  const scrollRef = useRef(null);

  // Save session ID and load history
  useEffect(() => {
    localStorage.setItem('snugglemind_chat_session', sessionId);
    getChatHistory(sessionId).then(res => {
      const history = res.data.messages || [];
      if (history.length > 0) {
        setMessages(history.map(m => ({ role: m.role, text: m.text })));
      } else {
        setMessages([{ role: 'assistant', text: "Hi! I'm the AI Snugglemind assistant. Ask me anything about bookkeeping, the platform features, or accounting concepts." }]);
      }
    }).catch(() => {
      setMessages([{ role: 'assistant', text: "Hi! I'm the AI Snugglemind assistant. Ask me anything about bookkeeping, the platform features, or accounting concepts." }]);
    }).finally(() => setHistoryLoaded(true));
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    // Persist user message
    saveChatMessage({ session_id: sessionId, role: 'user', text: userMsg, tab: 'ai' }).catch(() => {});
    setLoading(true);

    try {
      const res = await chatAiAssistant({ message: userMsg, session_id: sessionId });
      const reply = res.data.reply;
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
      // Persist AI reply
      saveChatMessage({ session_id: sessionId, role: 'assistant', text: reply, tab: 'ai' }).catch(() => {});
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={`ai-msg-${i}-${msg.role}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-zinc-950 text-white'
                : 'bg-violet-50 border border-violet-200 text-zinc-800'
            }`}>
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-1.5 mb-1">
                  <Robot size={12} weight="duotone" className="text-violet-500" />
                  <span className="text-[10px] uppercase tracking-widest text-violet-500">AI Assistant</span>
                </div>
              )}
              {msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-violet-50 border border-violet-200 rounded-sm">
              <Spinner size={16} className="text-violet-500 animate-spin" />
            </div>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-zinc-200 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Ask about bookkeeping, features..."
          className="rounded-sm h-9 text-sm flex-1"
          disabled={loading}
          data-testid="ai-chat-input"
        />
        <Button onClick={handleSend} disabled={loading || !input.trim()} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm h-9 px-3" data-testid="ai-chat-send">
          <PaperPlaneRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function SalesTab() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [messages, setMessages] = useState([]);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;
    setLoading(true);

    const userMsg = { role: 'user', text: form.message, name: form.name, email: form.email };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await chatSales(form);
      setMessages(prev => [...prev, { role: 'sales', text: res.data.reply }]);
      setForm(prev => ({ ...prev, message: '' }));
      setSent(true);
    } catch {
      setMessages(prev => [...prev, { role: 'sales', text: "Sorry, couldn't send. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-3">
        {/* Welcome message */}
        <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm text-sm">
          <div className="flex items-center gap-1.5 mb-1">
            <Handshake size={12} weight="duotone" className="text-blue-500" />
            <span className="text-[10px] uppercase tracking-widest text-blue-500">Sales Team</span>
          </div>
          Hi there! Leave us a message and our team will respond within 24 hours. Include your email so we can reach you.
        </div>

        {messages.map((msg, i) => (
          <div key={`sales-msg-${i}-${msg.role}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm ${
              msg.role === 'user' ? 'bg-zinc-950 text-white' : 'bg-blue-50 border border-blue-200 text-zinc-800'
            }`}>
              {msg.text}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSend} className="p-3 border-t border-zinc-200 space-y-2">
        {!sent && (
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name"
              className="rounded-sm h-8 text-xs"
              data-testid="sales-name-input"
            />
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              type="email"
              className="rounded-sm h-8 text-xs"
              data-testid="sales-email-input"
            />
          </div>
        )}
        <div className="flex gap-2">
          <Input
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Type your message..."
            className="rounded-sm h-9 text-sm flex-1"
            disabled={loading}
            data-testid="sales-message-input"
          />
          <Button type="submit" disabled={loading || !form.message.trim()} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-9 px-3" data-testid="sales-chat-send">
            <PaperPlaneRight size={16} />
          </Button>
        </div>
      </form>
    </div>
  );
}

function WhySnugglemindTab() {
  const [faqs, setFaqs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getWhySnugglemind()
      .then(res => setFaqs(res.data.faqs))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size={20} className="text-zinc-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-2">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={18} weight="duotone" className="text-emerald-500" />
          <h3 className="font-semibold text-sm">Why AI Snugglemind?</h3>
        </div>
        <p className="text-xs text-zinc-500 leading-relaxed">
          The bookkeeping AI that shows its work. Pattern recognition meets business context.
        </p>
      </div>

      {faqs.map((faq, i) => (
        <button
          key={faq.q || `faq-${i}`}
          onClick={() => setExpanded(expanded === i ? null : i)}
          className="w-full text-left bg-white border border-zinc-200 rounded-sm overflow-hidden transition-colors hover:border-zinc-300"
          data-testid={`faq-${i}`}
        >
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-sm font-medium pr-4">{faq.q}</span>
            {expanded === i ? <CaretDown size={14} className="text-zinc-400 flex-shrink-0" /> : <CaretRight size={14} className="text-zinc-400 flex-shrink-0" />}
          </div>
          {expanded === i && (
            <div className="px-4 pb-3 text-sm text-zinc-600 leading-relaxed border-t border-zinc-100 pt-3">
              {faq.a}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

export default function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ai');

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="support-chat-toggle"
        className={`fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200`}
        style={{ 
          background: isOpen ? '#2D3330' : '#0A3622',
          boxShadow: '0 4px 20px rgba(10,54,34,0.25)'
        }}
      >
        {isOpen ? (
          <X size={24} weight="bold" className="text-white" />
        ) : (
          <ChatCircleDots size={26} weight="fill" className="text-white" />
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-[8.5rem] right-6 z-50 w-[380px] h-[520px] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden"
          style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12)', border: '1px solid #E5E3DB' }}
          data-testid="support-chat-panel"
        >
          {/* Header */}
          <div className="text-white px-4 py-3" style={{ background: '#111815' }}>
            <div className="flex items-center gap-2 mb-3">
              <ChatCircleDots size={20} weight="fill" />
              <span className="font-semibold text-sm tracking-tight font-body">SnuggleMind Support</span>
            </div>
            {/* Tabs */}
            <div className="flex gap-1">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    data-testid={`chat-tab-${tab.key}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium font-body transition-colors ${
                      activeTab === tab.key
                        ? 'bg-white text-[#1A1A1A]'
                        : 'text-[#9CA3AF] hover:bg-white/10 hover:text-white'
                    }`}
                    style={activeTab !== tab.key ? { background: '#2D3330' } : {}}
                  >
                    <Icon size={13} weight={activeTab === tab.key ? 'fill' : 'regular'} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col min-h-0">
            {activeTab === 'ai' && <AiAssistantTab />}
            {activeTab === 'sales' && <SalesTab />}
            {activeTab === 'why' && <WhySnugglemindTab />}
          </div>
        </div>
      )}
    </>
  );
}
